import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getReportsMetadata, getAllReportGroups, getActiveRefreshesStatus } from "../../../services/reportService";
import { REPORT_KEYS } from "../queryKeys";
import { ReportMetadataLibrary } from "../../../models/pageModels/ReportLibrary";

export function useReportsMetadataQuery() {
  return useQuery<ReportMetadataLibrary>({
    queryKey: REPORT_KEYS.metadata,
    queryFn: getReportsMetadata,
  });
}

export function useGroupsQuery() {
  return useQuery<{ groupId: string; groupName: string; reportCount: number }[]>({
    queryKey: REPORT_KEYS.groups,
    queryFn: getAllReportGroups,
  });
}


export function useActiveRefreshesQuery(enabled = true) {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: REPORT_KEYS.activeRefreshes,
    queryFn: getActiveRefreshesStatus,
    enabled,
    // Poll while any group is refreshing. Reports no longer have their
    // own independent refresh state, so the only thing to watch is the
    // refreshingGroups array.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.refreshingGroups.length > 0) {
        return 5000;
      }
      return 30000;
    },
    refetchIntervalInBackground: true,
  });

  const prevData = useRef(query.data);
  
  useEffect(() => {
    if (!enabled) {
      prevData.current = undefined;
      return;
    }

    if (prevData.current && query.data) {
      // Detect groups that were refreshing in the previous tick but
      // are no longer refreshing in the current tick. Those are the
      // groups whose refresh just completed, and we need to invalidate
      // their dashboard caches and the metadata cache so the UI shows
      // fresh data.
      const prevGroups = prevData.current.refreshingGroups;
      const currentGroups = query.data.refreshingGroups;
      
      const finishedGroups = prevGroups.filter(id => !currentGroups.includes(id));

      if (finishedGroups.length > 0) {
         queryClient.invalidateQueries({ queryKey: REPORT_KEYS.metadata });
         finishedGroups.forEach(groupId => {
           queryClient.invalidateQueries({ queryKey: REPORT_KEYS.dashboard(groupId) });
         });
      }
    }
    prevData.current = query.data;
  }, [enabled, query.data, queryClient]);

  return query;
}
